import { Link } from 'react-router-dom'
import { Locale, PROVINCE_LABELS } from '../../types'
import { TipoLocaleBadge, FasciaPrezzoBadge, InEvidenzaBadge, ParcheggioGrandeBadge } from '../common/Badge'
import FavoritoButton from './FavoritoButton'
import { getImageUrl } from '../../utils/image'

interface LocaleCardProps {
  locale: Locale
  isFavorito?: boolean
  onFavoritoClick?: (localeId: number) => void
}

/**
 * Card per visualizzare un locale nella lista
 * Include immagine, badge, nome, località e specialità
 */
export default function LocaleCard({
  locale,
  isFavorito = false,
  onFavoritoClick,
}: LocaleCardProps) {
  // Costruisce URL immagine (gestisce path relativi del backend)
  const immagine = getImageUrl(locale.fotoPrincipale)

  // Mostra max 3 specialità, poi indica quante altre
  const specialitaVisibili = locale.specialita.slice(0, 3)
  const specialitaExtra = locale.specialita.length - 3

  return (
    <Link
      to={`/locale/${locale.slug}`}
      className="group block"
    >
      <article className="card h-full flex flex-col">
        {/* Immagine con overlay */}
        <div className="relative aspect-[16/9] overflow-hidden">
          <img
            src={immagine}
            alt={`${locale.nome} - ${locale.tipo}`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            loading="lazy"
            onError={(e) => {
              // Fallback se immagine non carica
              (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&h=400&fit=crop'
            }}
          />

          {/* Overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-night/80 via-transparent to-transparent" />

          {/* Badge in evidenza */}
          {locale.inEvidenza && (
            <div className="absolute top-3 left-3">
              <InEvidenzaBadge />
            </div>
          )}

          {/* Bottone preferiti */}
          <div className="absolute top-3 right-3">
            <FavoritoButton
              isFavorito={isFavorito}
              onClick={() => onFavoritoClick?.(locale.id)}
              size="sm"
            />
          </div>

          {/* Badge parcheggio grande */}
          {locale.parcheggioGrande && (
            <div className="absolute bottom-3 right-3">
              <ParcheggioGrandeBadge />
            </div>
          )}
        </div>

        {/* Contenuto */}
        <div className="p-4 flex flex-col flex-grow">
          {/* Badge tipo e prezzo */}
          <div className="flex flex-wrap gap-2 mb-2">
            <TipoLocaleBadge tipo={locale.tipo} />
            {locale.fasciaPrezzo && (
              <FasciaPrezzoBadge fascia={locale.fasciaPrezzo} />
            )}
          </div>

          {/* Nome */}
          <h3 className="font-display text-xl text-white group-hover:text-gold transition-colors truncate">
            {locale.nome}
          </h3>

          {/* Località */}
          <p className="text-sm text-gray-400 mt-1">
            {locale.comune}, {PROVINCE_LABELS[locale.provincia]}
          </p>

          {/* Specialità */}
          {locale.specialita.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-700/50 flex flex-wrap gap-1.5">
              {specialitaVisibili.map((spec, i) => (
                <span
                  key={i}
                  className="text-xs text-gray-400 bg-gray-800 px-2 py-0.5 rounded-full"
                >
                  {spec}
                </span>
              ))}
              {specialitaExtra > 0 && (
                <span className="text-xs text-gold px-2 py-0.5">
                  +{specialitaExtra}
                </span>
              )}
            </div>
          )}
        </div>
      </article>
    </Link>
  )
}
